import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Interface para MaterialAula
export interface MaterialAula {
  id: string
  aula_id: string
  nome_arquivo: string
  nome_original: string
  tipo_arquivo: string
  tamanho_arquivo: number
  url_arquivo: string
  bucket_name: string
  descricao: string | null
  ordem_exibicao: number
  status: string
  created_at: string
  updated_at: string
}

// Interface para DriverItem
export interface DriverItem {
  id: string
  user_id: string
  nome: string
  tipo: 'arquivo' | 'pasta'
  caminho: string
  tamanho: number
  tipo_mime?: string
  extensao?: string
  pai_id?: string
  compartilhado: boolean
  compartilhamento_token?: string
  senha_compartilhamento?: string
  exige_senha: boolean
  criado_em: string
  atualizado_em: string
  // Campos calculados
  url_arquivo?: string
}

// Tipos para upload de arquivos
export interface FileUpload {
  file: File
  aula_id: string
  descricao?: string
  ordem_exibicao?: number
}

export interface UploadProgress {
  progress: number
  status: 'uploading' | 'success' | 'error'
  message?: string
}

// Tipos para download de arquivos
export interface DownloadInfo {
  url: string
  filename: string
  size: number
  expires_at: string
}

// Tipos para o banco de dados
export type Database = {
  public: {
    Tables: {
      users: {
        Row: {
          id: string
          nome: string
          email: string
          grupo_id: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          nome: string
          email: string
          grupo_id: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          nome?: string
          email?: string
          grupo_id?: string
          created_at?: string
          updated_at?: string
        }
      }
      groups: {
        Row: {
          id: string
          nome: string
          permissoes: string[]
          created_at: string
        }
        Insert: {
          id?: string
          nome: string
          permissoes: string[]
          created_at?: string
        }
        Update: {
          id?: string
          nome?: string
          permissoes?: string[]
          created_at?: string
        }
      }
      corretores: {
        Row: {
          id: string
          user_id: string
          nome: string
          email: string
          telefone: string
          cep: string | null
          endereco: string | null
          cidade: string | null
          estado: string | null
          created_at: string
        }
        Insert: {
          id?: string
          user_id: string
          nome: string
          email: string
          telefone: string
          cep?: string | null
          endereco?: string | null
          cidade?: string | null
          estado?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          nome?: string
          email?: string
          telefone?: string
          cep?: string | null
          endereco?: string | null
          cidade?: string | null
          estado?: string | null
          created_at?: string
        }
      }
      construtoras: {
        Row: {
          id: string
          nome: string
          cnpj: string
          contato: string
          created_at: string
        }
        Insert: {
          id?: string
          nome: string
          cnpj: string
          contato: string
          created_at?: string
        }
        Update: {
          id?: string
          nome?: string
          cnpj?: string
          contato?: string
          created_at?: string
        }
      }
      imoveis: {
        Row: {
          id: string
          nome: string
          endereco: string
          tipo: string
          valor_base: number
          construtora_id: string
          tipo_id?: string
          created_at: string
        }
        Insert: {
          id?: string
          nome: string
          endereco: string
          tipo: string
          valor_base: number
          construtora_id: string
          tipo_id?: string
          created_at?: string
        }
        Update: {
          id?: string
          nome?: string
          endereco?: string
          tipo?: string
          valor_base?: number
          construtora_id?: string
          tipo_id?: string
          created_at?: string
        }
      }
      unidades: {
        Row: {
          id: string
          numero: string
          status: 'disponivel' | 'vendida' | 'reservada'
          imovel_id: string
          proprietario_id: string | null
          created_at: string
        }
        Insert: {
          id?: string
          numero: string
          status?: 'disponivel' | 'vendida' | 'reservada'
          imovel_id: string
          proprietario_id?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          numero?: string
          status?: 'disponivel' | 'vendida' | 'reservada'
          imovel_id?: string
          proprietario_id?: string | null
          created_at?: string
        }
      }
      proprietarios: {
        Row: {
          id: string
          nome: string
          cpf_cnpj: string
          contato: string
          created_at: string
        }
        Insert: {
          id?: string
          nome: string
          cpf_cnpj: string
          contato: string
          created_at?: string
        }
        Update: {
          id?: string
          nome?: string
          cpf_cnpj?: string
          contato?: string
          created_at?: string
        }
      }
      vendas: {
        Row: {
          id: string
          unidade_id: string
          corretor_id: string
          valor_venda: number
          data_venda: string
          created_at: string
        }
        Insert: {
          id?: string
          unidade_id: string
          corretor_id: string
          valor_venda: number
          data_venda: string
          created_at?: string
        }
        Update: {
          id?: string
          unidade_id?: string
          corretor_id?: string
          valor_venda?: number
          data_venda?: string
          created_at?: string
        }
      }
      comissoes: {
        Row: {
          id: string
          venda_id: string
          valor_comissao: number
          status: 'pendente' | 'pago'
          data_pagamento: string | null
          created_at: string
        }
        Insert: {
          id?: string
          venda_id: string
          valor_comissao: number
          status?: 'pendente' | 'pago'
          data_pagamento?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          venda_id?: string
          valor_comissao?: number
          status?: 'pendente' | 'pago'
          data_pagamento?: string | null
          created_at?: string
        }
      }
      bonus: {
        Row: {
          id: string
          corretor_id: string
          valor_bonus: number
          status: 'pendente' | 'pago'
          data_pagamento: string | null
          created_at: string
        }
        Insert: {
          id?: string
          corretor_id: string
          valor_bonus: number
          status?: 'pendente' | 'pago'
          data_pagamento?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          corretor_id?: string
          valor_bonus?: number
          status?: 'pendente' | 'pago'
          data_pagamento?: string | null
          created_at?: string
        }
      }
      tipos_imoveis: {
        Row: {
          id: string
          nome: string
          created_at: string
        }
        Insert: {
          id?: string
          nome: string
          created_at?: string
        }
        Update: {
          id?: string
          nome?: string
          created_at?: string
        }
      }
      planos: {
        Row: {
          id: string
          nome: string
          nome_display: string
          descricao: string | null
          preco_mensal: number
          preco_anual: number | null
          periodicidade: string
          periodo_trial: number
          recursos: any | null
          limite_vendas: number | null
          limite_corretores: number | null
          limite_imoveis: number | null
          suporte_prioritario: boolean
          relatorios_avancados: boolean
          api_access: boolean
          status: string
          ordem_exibicao: number
          data_criacao: string
          data_atualizacao: string
        }
        Insert: {
          id?: string
          nome: string
          nome_display: string
          descricao?: string | null
          preco_mensal: number
          preco_anual?: number | null
          periodicidade?: string
          periodo_trial?: number
          recursos?: any | null
          limite_vendas?: number | null
          limite_corretores?: number | null
          limite_imoveis?: number | null
          suporte_prioritario?: boolean
          relatorios_avancados?: boolean
          api_access?: boolean
          status?: string
          ordem_exibicao?: number
          data_criacao?: string
          data_atualizacao?: string
        }
        Update: {
          id?: string
          nome?: string
          nome_display?: string
          descricao?: string | null
          preco_mensal?: number
          preco_anual?: number | null
          periodicidade?: string
          periodo_trial?: number
          recursos?: any | null
          limite_vendas?: number | null
          limite_corretores?: number | null
          limite_imoveis?: number | null
          suporte_prioritario?: boolean
          relatorios_avancados?: boolean
          api_access?: boolean
          status?: string
          ordem_exibicao?: number
          data_criacao?: string
          data_atualizacao?: string
        }
      }
      assinaturas: {
        Row: {
          id: string
          user_id: string
          plano_id: string
          afiliado_id: string | null
          status: string
          data_inicio: string
          data_fim_trial: string | null
          data_proximo_pagamento: string | null
          data_cancelamento: string | null
          valor_mensal: number
          ciclo_pagamento: string
          metodo_pagamento: string | null
          comissao_paga: boolean
          valor_comissao: number | null
          data_pagamento_comissao: string | null
          observacoes: string | null
          dados_pagamento: any | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          plano_id: string
          afiliado_id?: string | null
          status?: string
          data_inicio?: string
          data_fim_trial?: string | null
          data_proximo_pagamento?: string | null
          data_cancelamento?: string | null
          valor_mensal: number
          ciclo_pagamento?: string
          metodo_pagamento?: string | null
          comissao_paga?: boolean
          valor_comissao?: number | null
          data_pagamento_comissao?: string | null
          observacoes?: string | null
          dados_pagamento?: any | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          plano_id?: string
          afiliado_id?: string | null
          status?: string
          data_inicio?: string
          data_fim_trial?: string | null
          data_proximo_pagamento?: string | null
          data_cancelamento?: string | null
          valor_mensal?: number
          ciclo_pagamento?: string
          metodo_pagamento?: string | null
          comissao_paga?: boolean
          valor_comissao?: number | null
          data_pagamento_comissao?: string | null
          observacoes?: string | null
          dados_pagamento?: any | null
          created_at?: string
          updated_at?: string
        }
      }
      material_aula: {
        Row: {
          id: string
          aula_id: string
          nome_arquivo: string
          nome_original: string
          tipo_arquivo: string
          tamanho_arquivo: number
          url_arquivo: string
          bucket_name: string
          descricao: string | null
          ordem_exibicao: number
          status: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          aula_id: string
          nome_arquivo: string
          nome_original: string
          tipo_arquivo: string
          tamanho_arquivo: number
          url_arquivo: string
          bucket_name?: string
          descricao?: string | null
          ordem_exibicao?: number
          status?: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          aula_id?: string
          nome_arquivo?: string
          nome_original?: string
          tipo_arquivo?: string
          tamanho_arquivo?: number
          url_arquivo?: string
          bucket_name?: string
          descricao?: string | null
          ordem_exibicao?: number
          status?: string
          created_at?: string
          updated_at?: string
        }
      }
    }
  }
}
